import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ContractsService } from '../contracts.service';
import { ContractRevisions } from '../models/contract-revisions.model';
import { MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { ContractStatus } from "../models/contract-status.model";
import { Contracts } from "../models/contracts.model";
import { GoogleCloudStorageService } from "../../services/google/google-cloud-storage.service";
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { SessionStorageService } from "../../services/session-storage.service";
import { FileSelectEvent, FileUploadModule } from "primeng/fileupload";
import { BankAccount } from '../../accounts/bank-account/bank-account.model';
import { BankAccountService } from '../../accounts/bank-account/bank-account.service';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { PanelModule } from "primeng/panel";
import { ProgressBarModule } from "primeng/progressbar";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';


@Component({
    selector: 'app-add-edit-contract-revision',
    templateUrl: './add-edit-contract-revision.component.html',
    styleUrls: ['./add-edit-contract-revision.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ToastModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    BreadcrumbModule,
    DatePickerModule,
    InputNumberModule,
    TextareaModule,
    CheckboxModule,
    ToggleSwitchModule,
    FileUploadModule,
    PanelModule,
    ProgressBarModule,
    FieldsetModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
],
    providers: [
        MessageService,
        ContractsService,
        GoogleCloudStorageService
    ]
})
export class AddEditContractRevisionComponent implements OnInit {

    contract: Contracts;
    contractId: number | null;
    contractRevisionId: number | null;
    createEditRevisionForm!: FormGroup;
    createEditRevision: ContractRevisions = new ContractRevisions();
    revisionCreationInProgress = false;
    firstPartyBankAccounts: BankAccount[] = [];
    bankAccountsLoading = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    private storage = getStorage();

    pdfUploadProgress: number = 0
    contractStatuses: ContractStatus[] = []; // Replace with your actual data
    isLoading: any;

    constructor(
        private fb: FormBuilder, private messageService: MessageService,
        private contractsService: ContractsService,
        private sessionStorageService: SessionStorageService,
        private googleCloudStorageService: GoogleCloudStorageService,
        private bankAccountService: BankAccountService,
        private route: ActivatedRoute,
        private location: Location
    ) { }

    ngOnInit(): void {
        this.createEditRevisionForm = this.fb.group({
            effectiveStartDateInput: [''],
            endDateInput: [''],
            totalAmountInput: [''],
            penaltyRateInput: [''],
            firstPartyAgreedInput: [false],
            secondPartyAgreedInput: [false],
            agreementPeriodInput: [''],
            nonRenewalDisclosureBeforeDaysInput: [''],
            goodsInsuranceByFirstPartyInput: [false],
            goodsInsuranceBySecondPartyInput: [false],
            vatAddedToInvoiceInput: [false],
            paymentTermsInput: [''],
            firstPartyBankAccountIdInput: [''],
            firstPartyBankAccountDetailsInput: [''],
            siteServiceTimeInput: [''],
            penaltyOnServiceTimeInput: [''],
            noticePeriodInput: [''],
            forceMajeureClausePeriodInput: [''],
            disputeResolutionInput: [''],
            governingLawInput: [''],
            revisionNotesInput: [''],
        });


        this.contractId = Number(this.route.snapshot.paramMap.get('contractId') ?? 0);
        console.log(this.contractId);
        this.contractsService.getContractById(this.contractId).subscribe(contract => {
            console.log(contract);
            this.contract = contract;
            this.loadFirstPartyBankAccounts();
            if (!this.contractRevisionId)
                this.createEditRevision.revisionNumber = this.contract.revisions.length + 1;
        });

        this.contractRevisionId = Number(this.route.snapshot.paramMap.get('contractRevisionId') ?? 0);
        console.log(this.contractRevisionId);
        if (this.contractRevisionId != 0) {
            this.loadContractRevision();
        }

        this.breadcrumbItems = [
            { label: 'Contracts', routerLink: '/contracts' },
            { label: this.createEditRevision.revisionID ? 'Edit Contract Revision' : 'Add Contract Revision' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        //this.contract = this.sessionStorageService.getCurrentContract()!;

        // Load contract statuses (replace with your actual data fetching logic)
        this.loadContractStatuses();
    }

    loadContractRevision() {
        this.contractsService.getRevisionById(this.contractId!, this.contractRevisionId!).subscribe(revision => {
            console.log(revision);
            this.createEditRevision = revision;
            this.updateFormValues();
        });
    }

    loadContractStatuses() {
        this.contractStatuses = Object.values(ContractStatus);
    }

    updateFormValues() {
        this.createEditRevisionForm.patchValue({
            effectiveStartDateInput: this.toDate(this.createEditRevision.effectiveStartDate),
            endDateInput: this.toDate(this.createEditRevision.endDate),
            totalAmountInput: this.createEditRevision.totalAmount,
            penaltyRateInput: this.createEditRevision.penaltyRate,
            firstPartyAgreedInput: this.createEditRevision.firstPartyAgreed,
            secondPartyAgreedInput: this.createEditRevision.secondPartyAgreed,
            agreementPeriodInput: this.createEditRevision.agreementPeriod,
            nonRenewalDisclosureBeforeDaysInput: this.createEditRevision.nonRenewalDisclosureBeforeDays,
            goodsInsuranceByFirstPartyInput: this.createEditRevision.goodsInsuranceByFirstParty,
            goodsInsuranceBySecondPartyInput: this.createEditRevision.goodsInsuranceBySecondParty,
            vatAddedToInvoiceInput: this.createEditRevision.vatAddedToInvoice,
            paymentTermsInput: this.createEditRevision.paymentTerms,
            firstPartyBankAccountIdInput: this.createEditRevision.firstPartyBankAccountId,
            firstPartyBankAccountDetailsInput: this.createEditRevision.firstPartyBankAccountDetails,
            siteServiceTimeInput: this.createEditRevision.siteServiceTime,
            penaltyOnServiceTimeInput: this.createEditRevision.penaltyOnServiceTime,
            noticePeriodInput: this.createEditRevision.noticePeriod,
            forceMajeureClausePeriodInput: this.createEditRevision.forceMajeureClausePeriod,
            disputeResolutionInput: this.createEditRevision.disputeResolution,
            governingLawInput: this.createEditRevision.governingLaw,
            revisionNotesInput: this.createEditRevision.revisionNotes,
        });
    }

    loadFirstPartyBankAccounts() {
        const firstPartyId = this.contract?.firstParty?.id;
        if (!firstPartyId) {
            this.firstPartyBankAccounts = [];
            return;
        }
        this.bankAccountsLoading = true;
        this.bankAccountService.getBankAccounts('Company', firstPartyId, '', 0, 1000).subscribe({
            next: response => {
                this.firstPartyBankAccounts = response.items ?? [];
                if (!this.contractRevisionId && !this.createEditRevision.firstPartyBankAccountId && this.firstPartyBankAccounts.length === 1) {
                    this.createEditRevision.firstPartyBankAccountId = this.firstPartyBankAccounts[0].id;
                    this.createEditRevisionForm.patchValue({ firstPartyBankAccountIdInput: this.firstPartyBankAccounts[0].id });
                    this.setFirstPartyBankAccountDetails(this.firstPartyBankAccounts[0].id);
                } else {
                    this.setFirstPartyBankAccountDetails(this.createEditRevision.firstPartyBankAccountId);
                }
                this.bankAccountsLoading = false;
            },
            error: () => {
                this.firstPartyBankAccounts = [];
                this.bankAccountsLoading = false;
            }
        });
    }

    onFirstPartyBankAccountChange(bankAccountId: number | null) {
        this.setFirstPartyBankAccountDetails(bankAccountId);
    }

    bankAccountLabel(account: BankAccount | undefined): string {
        if (!account) {
            return '';
        }
        const accountRef = account.accountNumber || account.iban || `ID ${account.id}`;
        return [account.accountName, account.bank?.name, accountRef, account.currencyCode]
            .filter(Boolean)
            .join(' - ');
    }

    private toDate(value: Date | string | number | null | undefined): Date | null {
        if (!value) {
            return null;
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    updateRevisionObject() {
        this.createEditRevision = {
            ...this.createEditRevision, // Keep existing properties
            effectiveStartDate: this.createEditRevisionForm.get('effectiveStartDateInput')?.value,
            endDate: this.createEditRevisionForm.get('endDateInput')?.value,
            totalAmount: this.createEditRevisionForm.get('totalAmountInput')?.value,
            penaltyRate: this.createEditRevisionForm.get('penaltyRateInput')?.value,
            firstPartyAgreed: this.createEditRevisionForm.get('firstPartyAgreedInput')?.value,
            secondPartyAgreed: this.createEditRevisionForm.get('secondPartyAgreedInput')?.value,
            agreementPeriod: this.createEditRevisionForm.get('agreementPeriodInput')?.value,
            nonRenewalDisclosureBeforeDays: this.createEditRevisionForm.get('nonRenewalDisclosureBeforeDaysInput')?.value,
            goodsInsuranceByFirstParty: this.createEditRevisionForm.get('goodsInsuranceByFirstPartyInput')?.value,
            goodsInsuranceBySecondParty: this.createEditRevisionForm.get('goodsInsuranceBySecondPartyInput')?.value,
            vatAddedToInvoice: this.createEditRevisionForm.get('vatAddedToInvoiceInput')?.value,
            paymentTerms: this.createEditRevisionForm.get('paymentTermsInput')?.value,
            firstPartyBankAccountId: this.toBankAccountId(this.createEditRevisionForm.get('firstPartyBankAccountIdInput')?.value),
            firstPartyBankAccountDetails: this.firstPartyBankAccountDetailsForPayload(),
            siteServiceTime: this.createEditRevisionForm.get('siteServiceTimeInput')?.value,
            penaltyOnServiceTime: this.createEditRevisionForm.get('penaltyOnServiceTimeInput')?.value,
            noticePeriod: this.createEditRevisionForm.get('noticePeriodInput')?.value,
            forceMajeureClausePeriod: this.createEditRevisionForm.get('forceMajeureClausePeriodInput')?.value,
            disputeResolution: this.createEditRevisionForm.get('disputeResolutionInput')?.value,
            governingLaw: this.createEditRevisionForm.get('governingLawInput')?.value,
            revisionNotes: this.createEditRevisionForm.get('revisionNotesInput')?.value,
        };
    }

    private setFirstPartyBankAccountDetails(bankAccountId: number | null | undefined) {
        const selected = this.firstPartyBankAccounts.find(account => account.id === Number(bankAccountId));
        if (!selected) {
            return;
        }
        const details = this.bankAccountLabel(selected);
        this.createEditRevision.firstPartyBankAccountDetails = details;
        this.createEditRevisionForm.patchValue({ firstPartyBankAccountDetailsInput: details });
    }

    private toBankAccountId(value: unknown): number {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        }
        if (value && typeof value === 'object' && 'id' in value) {
            const parsed = Number((value as BankAccount).id);
            return Number.isFinite(parsed) ? parsed : 0;
        }
        return 0;
    }

    private firstPartyBankAccountDetailsForPayload(): string {
        const manualDetails = this.createEditRevisionForm.get('firstPartyBankAccountDetailsInput')?.value;
        if (typeof manualDetails === 'string' && manualDetails.trim()) {
            return manualDetails.trim();
        }
        const selected = this.firstPartyBankAccounts.find(account =>
            account.id === this.toBankAccountId(this.createEditRevisionForm.get('firstPartyBankAccountIdInput')?.value)
        );
        return this.bankAccountLabel(selected) || 'To be confirmed';
    }



    createUpdateRevision() {
        this.revisionCreationInProgress = true;
        this.updateRevisionObject();
        const revisionData: ContractRevisions = this.createEditRevision;

        if (this.createEditRevision.revisionID) {
            // Update existing contract revision

            this.contractsService.updateRevision(this.contract.contractID, this.createEditRevision.revisionID, revisionData)
                .subscribe({
                    next: () => {
                        this.revisionCreationInProgress = false;
                        // Handle success, e.g., show a message, navigate back
                        this.location.back();
                    }, error: () => {
                        this.revisionCreationInProgress = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to update revision.'
                        });
                    }
                });
        } else {
            // Create new contract revision
            revisionData.createdBy = this.sessionStorageService.getCustomer()?.id ?? 0;
            revisionData.createdDate = new Date();
            if (!revisionData.originalAgreementDocumentUrl) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Original Agreement Document Required!'
                });
                this.revisionCreationInProgress = false;
                return;
            }
            this.contractsService.createRevision(this.contract.contractID, revisionData)
                .subscribe({
                    next: () => {
                        this.revisionCreationInProgress = false;
                        // Handle success, e.g., show a message, navigate back
                        this.location.back();
                    }, error: () => {
                        this.revisionCreationInProgress = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to create revision.'
                        });
                    }
                });
        }
    }

    clearRevisionForm() {
        this.createEditRevisionForm.reset();
    }

    onOriginalAgreementFileUpload(event: FileSelectEvent) {
        // Handle file upload logic here
        console.log(event.files);
        this.uploadPDFFile(event.files[0], 'ORIGINAL');
    }

    onSignedAgreementFileUpload(event: FileSelectEvent) {
        // Handle file upload logic here
        console.log(event.files);
        this.uploadPDFFile(event.files[0], 'SIGNED');
    }

    uploadPDFFile(file: File, fileType: string) {
        this.revisionCreationInProgress = true;

        // Create a unique file name
        const storageRef = ref(this.storage, file.name);

        // Upload the PDF file to Firebase Storage
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                this.pdfUploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                this.pdfUploadProgress = Math.round(this.pdfUploadProgress);
                if (this.pdfUploadProgress > 10)
                    this.pdfUploadProgress = this.pdfUploadProgress - 5;
                console.log('Upload is ' + this.pdfUploadProgress + '% done');
                switch (snapshot.state) {
                    case 'paused':
                        console.log('Upload is paused');
                        break;
                    case 'running':
                        console.log('Upload is running');
                        break;
                }
            },
            (error) => {
                // Handle unsuccessful uploads
                console.error("Error uploading file:", error);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'PDF upload failed.' });
                this.revisionCreationInProgress = false;
            },
            () => {
                // Handle successful uploads on complete
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    console.log('File available at', downloadURL);
                    if (fileType === 'ORIGINAL')
                        this.createEditRevision.originalAgreementDocumentUrl = downloadURL;
                    else this.createEditRevision.signedAgreementDocumentUrl = downloadURL;
                    this.revisionCreationInProgress = false;
                }).catch((error) => {
                    console.error("Error getting download URL:", error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get download URL.' });
                    this.revisionCreationInProgress = false;
                });
            }
        );
    }
}
